import {Component, computed, inject, input, output} from '@angular/core';
import {TaListBase} from '../../../pages/ta-list-base.directive';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {DatePipe, NgClass} from '@angular/common';
import {Chip} from 'primeng/chip';
import {InputText} from 'primeng/inputtext';
import {ApproverScope, toTAStatusText} from '../../../../util/app.types';
import {LocationService} from '../../../../services/location.service';
import {ScrollHeightDirective} from '../../../directives/scroll-height.directive';
import {PrepaidIndicatorComponent} from '../../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../attachment-indicator/attachment-indicator.component';
import {TaCostBubbleComponent} from '../../ta-cost-bubble/ta-cost-bubble.component';
import {BasicTaDetails, UserService} from '../../../../api';
import {ConfirmationWrapperService} from '../../../../services/confirmation-wrapper.service';
import {TaCommentCount} from '../../ta-comments/ta-comment-count';

/**
 * The scope-based approvals tabs: My Unit Approvals and My Project Approvals. Both are the same
 * table over /user/approvals with a different ?scope=, so one component covers them.
 *
 * Unlike the history tabs there is no store cache here. The endpoint returns a plain unpaged list
 * (the server passes limit -1), and the list has to be refetched after every approval anyway, so a
 * TTL cache would only ever serve a stale answer to the user who just changed it.
 */
@Component({
  selector: 'approver-scope-list',
  imports: [
    TaCommentCount,
    Button,
    Chip,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    TableModule,
    ScrollHeightDirective,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
    TaCostBubbleComponent,
  ],
  templateUrl: './approver-scope-list.component.html',
})
export class ApproverScopeListComponent extends TaListBase {
  scope = input.required<ApproverScope>();

  // The pending queue on the parent page overlaps these lists, so it has to refetch when an
  // approval is actioned from here.
  approved = output<void>();

  private userApi = inject(UserService);
  private confirmationServiceWrapper = inject(ConfirmationWrapperService);
  protected locationService = inject(LocationService);

  // Drives the testids as well as the copy, so each tab stays independently addressable.
  testIdPrefix = computed(() => this.scope() === 'unit' ? 'approvals-unit' : 'approvals-project');
  emptyMessage = computed(() => this.scope() === 'unit'
    ? 'There are no active TAs against your units\' projects.'
    : 'There are no active TAs against the projects you approve.');

  isLoading = computed(() => this.store.isLoadingData());

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadTaList();
    this.locationService.loadLocations();
  }

  reloadTaList(): void {
    this.loadTaList();
  }

  private loadTaList(): void {
    this.store.updateLoadingData(true);
    this.userApi.getUserApprovals(this.scope()).subscribe({
      next: result => {
        // Sorted here rather than server-side for the same reason the pending queue is: the scope
        // legs go through SearchSubmissions, whose default order is not newest-first.
        this.availableTaList.set([...(result.data ?? [])].sort((a, b) => {
          const da = a.created?.created ? new Date(a.created.created).getTime() : 0;
          const db = b.created?.created ? new Date(b.created.created).getTime() : 0;
          return db - da;
        }));
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  /**
   * Whether to render the row's Approve TA action at all. These tabs are oversight lists - most of
   * what they show is approved by someone else - so a row action the user cannot use is left out
   * rather than shown disabled. That leaves it for the global permission holders (Finance Admins,
   * System Admins), matched to the status: travel approval for PendingApproval, acquittal approval
   * for AcquittalComplete, cancellation approval for PendingCancelApproval.
   */
  canApproveTaSubmission(taSubmission: BasicTaDetails): boolean {
    if (taSubmission.status === 'PendingApproval') {
      return this.permissionService.canApproveAnyTravel();
    }
    if (taSubmission.status === 'AcquittalComplete') {
      return this.permissionService.canApproveAnyAcquittal();
    }
    if (taSubmission.status === 'PendingCancelApproval') {
      return this.permissionService.canApproveAnyCancellation();
    }
    return false;
  }

  approveTaSubmission(taSubmission: BasicTaDetails): void {
    // The button isn't rendered for any other status or without the global permission; guarded so a
    // stale row can't be pushed into the wrong confirm dialog.
    if (!this.canApproveTaSubmission(taSubmission)) {
      return;
    }

    const confirm = taSubmission.status === 'AcquittalComplete'
      ? this.confirmationServiceWrapper.approveAcquittalConfirm('approve', '', taSubmission)
      : taSubmission.status === 'PendingCancelApproval'
        ? this.confirmationServiceWrapper.approveCancellationConfirm('', taSubmission)
        : this.confirmationServiceWrapper.approveConfirm('approve', '', taSubmission);

    confirm.subscribe({
      next: () => {
        this.loadTaList();
        this.approved.emit();
      },
      error: err => {
        console.error(err);
      },
    });
  }

  protected readonly toTAStatusText = toTAStatusText;
}
